import { debounce } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import React, { useMemo } from 'react';

/**
 * Custom hook for managing data table functionality.
 * @returns {Object} An object containing the apiRef and handleSearchValueChange function.
 */
const useDataTable = () => {
  // Reference to the grid API
  const apiRef = useGridApiRef();

  /**
   * Memoized debounced function to update the search value in the grid.
   * @type {Function}
   */
  const updateSearchValue = useMemo(() => {
    return debounce((newValue) => {
      apiRef?.current?.setQuickFilterValues(newValue);
    }, 500);
  }, [apiRef]);

  /**
   * Handles changes in the search input value.
   * @param {React.ChangeEvent<HTMLInputElement> | string} event - The change event or new value.
   * @param {Function} [setSearchValue] - Optional function to set the search value externally.
   */
  const handleSearchValueChange = (
    event: React.ChangeEvent<HTMLInputElement> | string,
    setSearchValue?: (params: string) => void,
  ) => {
    const newValue = typeof event === 'string' ? event : event.target.value;
    setSearchValue?.(newValue);
    updateSearchValue(newValue);
  };

  return { apiRef, handleSearchValueChange };
};

export default useDataTable;
